import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff, Lock, ShieldCheck, MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { TAGLINE } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMeta } from "@/lib/useMeta";

const NAVY = "#0A1628";
const CARD = "#112240";
const GOLD = "#C9A84C";

const DEMO = [
  ["Project Manager", "pm@plansarthi.com", "Pm@1234"],
  ["HOD", "hod@plansarthi.com", "Hod@1234"],
  ["Site Engineer", "engineer@plansarthi.com", "Eng@1234"],
  ["Storekeeper", "store@plansarthi.com", "Store@1234"],
];

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden>
    <path fill="#4285F4" d="M21.6 12.2c0-.8-.07-1.35-.2-1.95H12v3.7h5.5c-.11.9-.71 2.26-2 3.17l-.02.12 2.9 2.25.2.02c1.85-1.7 2.92-4.2 2.92-7.31z" />
    <path fill="#34A853" d="M12 22c2.7 0 4.96-.89 6.6-2.42l-3.14-2.44c-.84.59-1.97 1-3.46 1a5.36 5.36 0 0 1-5.07-3.7l-.11.01-2.98 2.31-.04.11A10 10 0 0 0 12 22z" />
    <path fill="#FBBC05" d="M6.93 14.44A6.6 6.6 0 0 1 6.57 12c0-.85.15-1.67.35-2.44l-.01-.16-3.02-2.35-.1.05A10 10 0 0 0 2.7 12c0 1.61.39 3.13 1.09 4.46l3.14-2.02z" />
    <path fill="#EA4335" d="M12 5.86c1.88 0 3.15.81 3.87 1.49l2.83-2.76C16.95 2.99 14.7 2 12 2 8.09 2 4.71 4.24 3.09 7.5l3.13 2.44A5.38 5.38 0 0 1 12 5.86z" />
  </svg>
);

const fieldStyle = {
  background: "rgba(255,255,255,0.04)",
  borderColor: "rgba(201,168,76,0.22)",
  color: "#EAF0F7",
};

export default function Login() {
  useMeta({
    title: "Sign In | Plan-Sarthi EPC Project Management Platform",
    description:
      "Sign in to your Plan-Sarthi workspace to manage WBS, daily progress reports, critical path, earned value and AI risk forecasting for your EPC projects.",
    path: "/login",
  });
  const { login, setSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [lockedFor, setLockedFor] = useState(0);
  const [stage, setStage] = useState("password");
  const [otp, setOtp] = useState("");
  const [otpNote, setOtpNote] = useState("");

  useEffect(() => {
    const reason = params.get("reason");
    if (reason === "expired") toast.error("Your session has expired. Please log in again.");
    if (reason === "auth") toast.error("Please log in to access your workspace.");
    if (params.get("verified") === "1") toast.success("Email verified — you can sign in now.");
    if (params.get("reset") === "1") toast.success("Password updated. Please sign in.");
  }, [params]);

  useEffect(() => {
    if (lockedFor <= 0) return;
    const t = setInterval(() => setLockedFor((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [lockedFor]);

  const loadCaptcha = async () => {
    const { data } = await api.get("/auth/captcha");
    setCaptcha(data);
    setCaptchaAnswer("");
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/projects";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (lockedFor > 0) return;
    setBusy(true);
    try {
      const result = await login(email, password, captcha?.captcha_id, captchaAnswer);
      if (result?.otp_required) {
        setStage("otp");
        setOtpNote(result.sent_to ? `Code sent to ${result.sent_to}` : "");
        toast.success("Verification code sent");
        return;
      }
      toast.success("Signed in to Plan-Sarthi");
      navigate(result?.role === "superadmin" ? "/admin" : "/projects");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "EMAIL_NOT_VERIFIED") {
        toast.error("Your email isn't verified yet. Check your inbox for the verification link.");
        navigate("/signup");
        return;
      }
      if (detail && typeof detail === "object") {
        if (detail.locked_for > 0) {
          setLockedFor(detail.locked_for);
          toast.error(detail.message || "Account temporarily locked.");
        } else {
          toast.error(
            `${detail.message || "Sign in failed"}${
              detail.attempts_left != null ? ` — ${detail.attempts_left} attempt(s) left before a 15-minute lockout` : ""
            }`
          );
        }
        if (detail.captcha_required) await loadCaptcha();
        else setCaptcha(null);
        return;
      }
      toast.error(apiError(detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login-otp", { email, otp });
      localStorage.setItem("cx_token", data.access_token);
      localStorage.setItem("cx_user", JSON.stringify(data));
      setSession(data);
      toast.success("Verified — opening console");
      navigate(data.redirect || "/projects");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const mmss = `${String(Math.floor(lockedFor / 60)).padStart(2, "0")}:${String(lockedFor % 60).padStart(2, "0")}`;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-5 py-12"
      style={{ background: NAVY, fontFamily: "Inter, system-ui, sans-serif" }}
      data-testid="login-page"
    >
      <Link to="/" className="flex items-baseline gap-0 mb-8 select-none" data-testid="login-logo">
        <span className="text-2xl font-bold tracking-tight text-white">Plan</span>
        <span className="text-2xl font-bold tracking-tight" style={{ color: GOLD }}>-</span>
        <span className="text-2xl font-bold tracking-tight text-white">Sarthi</span>
      </Link>

      <div
        className="w-full max-w-[420px] border p-7 sm:p-9"
        style={{ background: CARD, borderColor: "rgba(201,168,76,0.18)", boxShadow: "0 24px 60px rgba(0,0,0,0.45)" }}
      >
        {stage === "password" ? (
          <>
            <h1 className="text-white text-2xl font-semibold tracking-tight">Login</h1>
            <p className="text-[13px] mt-1.5" style={{ color: "rgba(234,240,247,0.55)" }}>
              {TAGLINE}
            </p>

            {lockedFor > 0 ? (
              <div
                className="mt-5 flex items-center gap-2 border px-3 py-2.5 text-[12.5px]"
                style={{ borderColor: "rgba(239,68,68,0.4)", background: "rgba(239,68,68,0.08)", color: "#FCA5A5" }}
                data-testid="lockout-notice"
              >
                <Lock className="w-4 h-4 shrink-0" />
                Too many failed attempts. Try again in <strong className="tabular-nums">{mmss}</strong>
              </div>
            ) : null}

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "rgba(234,240,247,0.6)" }}>
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={fieldStyle}
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "rgba(234,240,247,0.6)" }}>
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    style={{ ...fieldStyle, paddingRight: "2.5rem" }}
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 transition-opacity duration-150 hover:opacity-100 opacity-60"
                    style={{ color: "#EAF0F7" }}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    data-testid="toggle-password-visibility"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {captcha ? (
                <div className="space-y-1.5" data-testid="captcha-block">
                  <Label className="text-[11px] uppercase tracking-[0.14em]" style={{ color: GOLD }}>
                    Security check — {captcha.question}
                  </Label>
                  <Input
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value)}
                    inputMode="numeric"
                    placeholder="Your answer"
                    required
                    style={fieldStyle}
                    data-testid="captcha-input"
                  />
                </div>
              ) : null}

              <Button
                type="submit"
                className="w-full font-semibold transition-transform duration-150 active:scale-[0.99]"
                style={{ background: GOLD, color: NAVY }}
                disabled={busy || lockedFor > 0}
                data-testid="login-submit-btn"
              >
                {busy ? "Authenticating…" : "Login"}
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </form>

            <div className="mt-5 flex flex-col gap-2 text-[12.5px]">
              <Link
                to="/forgot-password"
                className="transition-colors duration-150 hover:opacity-80"
                style={{ color: GOLD }}
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </Link>
              <span style={{ color: "rgba(234,240,247,0.55)" }}>
                Don&apos;t have an account?{" "}
                <Link to="/signup" className="font-medium hover:underline" style={{ color: GOLD }} data-testid="login-signup-link">
                  Start Free Trial
                </Link>
              </span>
            </div>

            <div className="flex items-center gap-3 my-5">
              <span className="h-px flex-1" style={{ background: "rgba(201,168,76,0.2)" }} />
              <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "rgba(234,240,247,0.35)" }}>
                or
              </span>
              <span className="h-px flex-1" style={{ background: "rgba(201,168,76,0.2)" }} />
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full gap-2 border transition-colors duration-150"
              style={{ background: "transparent", borderColor: "rgba(201,168,76,0.3)", color: "#EAF0F7" }}
              onClick={googleLogin}
              data-testid="google-login-btn"
            >
              <GoogleMark /> Continue with Google
            </Button>

            <div className="mt-6 border" style={{ borderColor: "rgba(201,168,76,0.16)" }}>
              <div
                className="flex items-center gap-1.5 px-3 py-2 border-b text-[10.5px] uppercase tracking-[0.14em]"
                style={{ borderColor: "rgba(201,168,76,0.16)", color: "rgba(234,240,247,0.45)" }}
              >
                <ShieldCheck className="w-3.5 h-3.5" /> Role-based demo logins
              </div>
              <div>
                {DEMO.map(([role, mail, pass]) => (
                  <button
                    key={mail}
                    type="button"
                    onClick={() => {
                      setEmail(mail);
                      setPassword(pass);
                    }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left border-b last:border-b-0 transition-colors duration-150 hover:bg-white/5"
                    style={{ borderColor: "rgba(201,168,76,0.1)" }}
                    data-testid={`demo-login-${role.replace(/\s/g, "-").toLowerCase()}`}
                  >
                    <span className="text-[12.5px] shrink-0" style={{ color: "#EAF0F7" }}>
                      {role}
                    </span>
                    <span className="text-[11px] truncate" style={{ color: "rgba(234,240,247,0.4)" }}>
                      {mail}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div data-testid="login-otp-stage">
            <MailCheck className="w-8 h-8" style={{ color: GOLD }} />
            <h1 className="text-white text-2xl font-semibold tracking-tight mt-3">Verification required</h1>
            <p className="text-[13px] mt-1.5" style={{ color: "rgba(234,240,247,0.6)" }}>
              Enter the 6-digit code sent to your registered address to finish signing in.
              {otpNote ? <span className="block mt-1 text-[12px]" style={{ color: GOLD }}>{otpNote}</span> : null}
            </p>
            <form onSubmit={submitOtp} className="mt-6 space-y-4">
              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                placeholder="000000"
                className="text-lg tracking-[0.4em] text-center"
                style={fieldStyle}
                data-testid="login-otp-input"
              />
              <Button
                type="submit"
                className="w-full font-semibold"
                style={{ background: GOLD, color: NAVY }}
                disabled={busy || otp.length !== 6}
                data-testid="login-otp-submit-btn"
              >
                {busy ? "Verifying…" : "Verify & continue"}
              </Button>
            </form>
            <button
              type="button"
              onClick={() => {
                setStage("password");
                setOtp("");
              }}
              className="mt-4 text-[12.5px] hover:underline"
              style={{ color: "rgba(234,240,247,0.5)" }}
              data-testid="login-otp-back-btn"
            >
              ← Back to login
            </button>
          </div>
        )}
      </div>

      <p className="mt-7 text-[11px] text-center" style={{ color: "rgba(234,240,247,0.3)" }}>
        Plan-Sarthi · A Sarthi Associates product ·{" "}
        <Link to="/privacy-policy" className="hover:underline">Privacy</Link> ·{" "}
        <Link to="/terms-of-service" className="hover:underline">Terms</Link>
      </p>
    </div>
  );
}
